import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import api from '../services/api';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { Users, MessageSquare, Circle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';

// Online status indicator dot
const OnlineIndicator = ({ isOnline, size = 'sm' }) => {
  const sizeClasses = {
    sm: 'w-2.5 h-2.5',
    md: 'w-3 h-3',
    lg: 'w-4 h-4'
  };

  return (
    <span className={`inline-block rounded-full ${sizeClasses[size]} ${
      isOnline 
        ? 'bg-green-500 shadow-[0_0_6px_rgba(34,197,94,0.6)]' 
        : 'bg-zinc-500'
    }`} />
  );
};

// Format last seen time
const formatLastSeen = (lastSeen, language) => {
  if (!lastSeen) return '';
  
  const locale = language === 'fr' ? fr : enUS;
  const date = new Date(lastSeen);
  
  return formatDistanceToNow(date, { addSuffix: true, locale });
};

// Friend item in the list
const FriendItem = ({ friend, language, onMessage }) => {
  const navigate = useNavigate();

  return (
    <div className="flex items-center justify-between p-3 hover:theme-bg-card rounded-xl transition-colors">
      <div className="flex items-center gap-3">
        {/* Avatar with online indicator */}
        <div className="relative">
          <div 
            className="w-10 h-10 rounded-full flex items-center justify-center font-bold"
            style={{ 
              backgroundColor: `${friend.avatarColor || '#00d4ff'}20`,
              color: friend.avatarColor || '#00d4ff'
            }}
          >
            {(friend.nickname || friend.firstName || friend.name)?.[0]?.toUpperCase() || 'U'}
          </div>
          <span className="absolute bottom-0 right-0 transform translate-x-0.5 translate-y-0.5">
            <OnlineIndicator isOnline={friend.isOnline} />
          </span>
        </div>

        {/* Name and status */}
        <div>
          <p className="font-medium theme-text">
            {friend.nickname ? `@${friend.nickname}` : (friend.firstName || friend.name?.split(' ')[0] || friend.name)}
          </p>
          <p className="text-xs theme-text-secondary">
            {friend.isOnline 
              ? (language === 'fr' ? 'En ligne' : 'Online')
              : formatLastSeen(friend.lastSeen, language)
            }
          </p>
        </div>
      </div>

      {/* Message button */}
      <Button
        variant="ghost"
        size="icon"
        onClick={() => navigate(`/messages?user=${friend.id}`)}
        className="theme-text-secondary hover:theme-accent"
        data-testid={`message-friend-${friend.id}`}
      >
        <MessageSquare size={18} />
      </Button>
    </div>
  );
};

// Main component: Friends with online status
const OnlineFriends = () => {
  const { language, t } = useTranslation();
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [onlineCount, setOnlineCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  const loadFriendsStatus = async () => {
    try {
      const { data } = await api.get(`/presence/friends`, {
        withCredentials: true
      });
      setFriends(data.friends);
      setOnlineCount(data.onlineCount);
    } catch (error) {
      console.error('Error loading friends status:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadFriendsStatus();
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="theme-text hover:theme-accent relative"
          data-testid="online-friends-button"
        >
          <Users size={24} />
          {onlineCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-green-500 theme-text text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
              {onlineCount > 9 ? '9+' : onlineCount}
            </span>
          )}
        </Button>
      </DialogTrigger>

      <DialogContent className="theme-bg-card theme-border theme-text max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users size={20} className="theme-accent" />
            {language === 'fr' ? 'Amis' : 'Friends'}
            {onlineCount > 0 && (
              <span className="text-sm font-normal text-green-500">
                ({onlineCount} {language === 'fr' ? 'en ligne' : 'online'})
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="max-h-80 overflow-y-auto">
          {loading ? (
            <div className="text-center py-8 theme-text-secondary">
              {language === 'fr' ? 'Chargement...' : 'Loading...'}
            </div>
          ) : friends.length === 0 ? (
            <div className="text-center py-8 theme-text-secondary">
              <Users size={48} className="mx-auto mb-3 text-zinc-700" />
              <p>{language === 'fr' ? 'Aucun ami pour le moment' : 'No friends yet'}</p>
            </div>
          ) : (
            <div className="space-y-1">
              {/* Online friends first */}
              {friends.filter(f => f.isOnline).length > 0 && (
                <div className="mb-4">
                  <p className="text-xs text-green-500 font-medium px-3 mb-2 flex items-center gap-1">
                    <Circle size={8} className="fill-green-500" />
                    {language === 'fr' ? 'EN LIGNE' : 'ONLINE'}
                  </p>
                  {friends.filter(f => f.isOnline).map(friend => (
                    <FriendItem key={friend.id} friend={friend} language={language} />
                  ))}
                </div>
              )}

              {/* Offline friends */}
              {friends.filter(f => !f.isOnline).length > 0 && (
                <div>
                  <p className="text-xs theme-text-secondary font-medium px-3 mb-2">
                    {language === 'fr' ? 'HORS LIGNE' : 'OFFLINE'}
                  </p>
                  {friends.filter(f => !f.isOnline).map(friend => (
                    <FriendItem key={friend.id} friend={friend} language={language} />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export { OnlineIndicator, OnlineFriends };
export default OnlineFriends;
